/**
 * Payment-terms admin — `/settings/payment-terms` (SPEC screen 5).
 *
 * `early_discount_pct` is `PartnersPercentageString`: NUMERIC(5,2) as a
 * decimal STRING with exactly two fractional digits and never "0.00". Typed
 * text is canonicalised by string padding, never through `Number()`. Day
 * counts are plain bounded integers, which is what the contract declares.
 *
 * Rows are archived through DELETE (no If-Match) and restored through the
 * merge-PATCH `status` field, which DOES need the row's strong validator —
 * the list carries none, so restore reads the exact entity first.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@erp/ui";

import { DataTable, badgeCell, textCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { formatDate, formatQuantity } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  archivePaymentTerm,
  createPaymentTerm,
  getPaymentTerm,
  updatePaymentTerm,
  type PaymentTerm,
  type PaymentTermCreate,
  type PaymentTermPatch,
} from "../api";
import { PERCENTAGE_FRACTION_DIGITS, toBoundedInteger, toPercentageString } from "../decimal";
import { partnersKeys, usePaymentTerms } from "../queries";

const CODE_RE = /^[a-z][a-z0-9_]*$/;

interface TermDraft {
  code: string;
  name: string;
  netDays: string;
  dayOfMonthDue: string;
  earlyDiscountPct: string;
  discountDays: string;
}

const EMPTY_DRAFT: TermDraft = {
  code: "",
  name: "",
  netDays: "30",
  dayOfMonthDue: "",
  earlyDiscountPct: "",
  discountDays: "",
};

interface ValidatedTerm {
  netDays: number;
  dayOfMonthDue: number | null;
  earlyDiscountPct: string | null;
  discountDays: number | null;
}

export interface PaymentTermsPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canArchive?: boolean;
}

export function PaymentTermsPage({
  canCreate = true,
  canUpdate = true,
  canArchive = true,
}: PaymentTermsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const termsQuery = usePaymentTerms();
  const terms = termsQuery.data ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ id: string; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<TermDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Partial<Record<keyof TermDraft, string>>>({});
  const [archiveTarget, setArchiveTarget] = useState<PaymentTerm | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: partnersKeys.paymentTerms.all });
  };

  const loadForEdit = useMutation({
    mutationFn: (paymentTermId: string) => getPaymentTerm(paymentTermId),
    onSuccess: ({ data, etag }) => {
      setEditing({ id: data.id, etag });
      setDraft({
        code: data.code,
        name: data.name,
        netDays: String(data.net_days),
        dayOfMonthDue: data.day_of_month_due === undefined ? "" : String(data.day_of_month_due),
        earlyDiscountPct: data.early_discount_pct ?? "",
        discountDays: data.discount_days === undefined ? "" : String(data.discount_days),
      });
      setErrors({});
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const createMutation = useIdempotentMutation<PaymentTerm, PaymentTermCreate>({
    mutationFn: (input, key) => createPaymentTerm(input, key),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        setErrors({ code: error.problem?.detail ?? t("partners.paymentTerms.codeRequired") });
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: PaymentTermPatch; ifMatch: string }) =>
      updatePaymentTerm(input.id, input.patch, input.ifMatch),
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const archiveMutation = useMutation({
    mutationFn: (paymentTermId: string) => archivePaymentTerm(paymentTermId),
    onSuccess: () => {
      setArchiveTarget(null);
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const restoreMutation = useMutation({
    // The list response carries no per-row validator; read the exact entity
    // and forward its resource-bound ETag rather than inventing one.
    mutationFn: async (paymentTermId: string) => {
      const { etag } = await getPaymentTerm(paymentTermId);
      if (etag === null) throw new ApiError("Missing payment-term ETag; reload and retry", 428);
      return updatePaymentTerm(paymentTermId, { status: "active" }, etag);
    },
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const set = <K extends keyof TermDraft>(key: K, value: TermDraft[K]): void => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validate = (): ValidatedTerm | null => {
    const next: Partial<Record<keyof TermDraft, string>> = {};
    if (editing === null && !CODE_RE.test(draft.code.trim())) {
      next.code = t("partners.paymentTerms.codeRequired");
    }
    if (draft.name.trim() === "") next.name = t("partners.paymentTerms.nameRequired");

    const netDays = toBoundedInteger(draft.netDays, 0, 3650);
    if (netDays === null) next.netDays = t("partners.paymentTerms.netDaysInvalid");

    let dayOfMonthDue: number | null = null;
    if (draft.dayOfMonthDue.trim() !== "") {
      dayOfMonthDue = toBoundedInteger(draft.dayOfMonthDue, 1, 31);
      if (dayOfMonthDue === null) next.dayOfMonthDue = t("partners.paymentTerms.dayOfMonthInvalid");
    }

    let earlyDiscountPct: string | null = null;
    if (draft.earlyDiscountPct.trim() !== "") {
      earlyDiscountPct = toPercentageString(draft.earlyDiscountPct);
      if (earlyDiscountPct === null) {
        next.earlyDiscountPct = t("partners.paymentTerms.percentInvalid");
      }
    }

    let discountDays: number | null = null;
    if (draft.discountDays.trim() !== "") {
      discountDays = toBoundedInteger(draft.discountDays, 0, 3650);
      if (discountDays === null) next.discountDays = t("partners.paymentTerms.netDaysInvalid");
    }
    if (earlyDiscountPct !== null && discountDays === null) {
      next.discountDays = t("partners.paymentTerms.discountDaysRequired");
    }

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return null;
    }
    setErrors({});
    return {
      netDays: netDays as number,
      dayOfMonthDue,
      earlyDiscountPct,
      discountDays,
    };
  };

  const submit = (): void => {
    const validated = validate();
    if (validated === null) return;
    if (editing === null) {
      createMutation.mutate({
        code: draft.code.trim(),
        name: draft.name.trim(),
        net_days: validated.netDays,
        ...(validated.dayOfMonthDue === null ? {} : { day_of_month_due: validated.dayOfMonthDue }),
        ...(validated.earlyDiscountPct === null
          ? {}
          : { early_discount_pct: validated.earlyDiscountPct }),
        ...(validated.discountDays === null ? {} : { discount_days: validated.discountDays }),
      });
      return;
    }
    if (editing.etag === null) {
      setErrors({ name: t("partners.paymentTerms.nameRequired") });
      return;
    }
    updateMutation.mutate({
      id: editing.id,
      ifMatch: editing.etag,
      patch: {
        name: draft.name.trim(),
        net_days: validated.netDays,
        day_of_month_due: validated.dayOfMonthDue,
        early_discount_pct: validated.earlyDiscountPct,
        discount_days: validated.discountDays,
      },
    });
  };

  /** Display-only illustration of the rule; the backend owns the real dates. */
  const preview = useMemo(() => {
    const netDays = toBoundedInteger(draft.netDays, 0, 3650);
    if (netDays === null) return null;
    const today = new Date();
    const iso = (offsetDays: number): string => {
      const date = new Date(today.getTime());
      date.setUTCDate(date.getUTCDate() + offsetDays);
      return date.toISOString();
    };
    const invoiced = formatDate(iso(0), i18n.language);
    const due = formatDate(iso(netDays), i18n.language);
    const pct = toPercentageString(draft.earlyDiscountPct);
    const discountDays = toBoundedInteger(draft.discountDays, 0, 3650);
    if (pct === null || discountDays === null) {
      return t("partners.paymentTerms.previewNoDiscount", { invoiced, due });
    }
    return t("partners.paymentTerms.preview", {
      invoiced,
      due,
      pct: formatQuantity(pct, i18n.language, PERCENTAGE_FRACTION_DIGITS),
      discountDate: formatDate(iso(discountDays), i18n.language),
    });
  }, [draft.netDays, draft.earlyDiscountPct, draft.discountDays, i18n.language, t]);

  const columns = useMemo<ColumnDef<PaymentTerm>[]>(
    () => [
      {
        accessorKey: "code",
        id: "code",
        header: t("partners.paymentTerms.columns.code"),
        enableSorting: false,
        cell: (ctx) => (
          <span className="flex items-center gap-2">
            <span dir="ltr" className="font-mono text-xs">
              {ctx.getValue<string>()}
            </span>
            {ctx.row.original.is_seeded ? (
              <Badge variant="outline">{t("partners.common.seededBadge")}</Badge>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t("partners.paymentTerms.columns.name"),
        enableSorting: false,
        cell: (ctx) => <span dir="auto">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "net_days",
        id: "net_days",
        header: t("partners.paymentTerms.columns.netDays"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => String(ctx.getValue<number>()),
      },
      {
        accessorKey: "day_of_month_due",
        id: "day_of_month_due",
        header: t("partners.paymentTerms.columns.dayOfMonth"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => textCell(ctx.getValue<number | undefined>()),
      },
      {
        accessorKey: "early_discount_pct",
        id: "early_discount_pct",
        header: t("partners.paymentTerms.columns.discount"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          if (value === undefined) return textCell(undefined);
          return (
            <span dir="ltr" className="tabular-nums">
              {formatQuantity(value, i18n.language, PERCENTAGE_FRACTION_DIGITS)}%
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("partners.paymentTerms.columns.status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<PaymentTerm["status"]>();
          return badgeCell(
            value,
            { active: "success", archived: "secondary" },
            t(`partners.status.${value}`),
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: (ctx) => {
          const term = ctx.row.original;
          return (
            <span className="flex flex-wrap items-center justify-end gap-1">
              {canUpdate && term.status === "active" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={loadForEdit.isPending}
                  onClick={() => loadForEdit.mutate(term.id)}
                >
                  {t("partners.paymentTerms.editTitle")}
                </Button>
              ) : null}
              {canArchive && term.status === "active" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setArchiveTarget(term)}
                >
                  {t("partners.common.archive")}
                </Button>
              ) : null}
              {canUpdate && term.status === "archived" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={restoreMutation.isPending}
                  onClick={() => restoreMutation.mutate(term.id)}
                >
                  {t("partners.common.restore")}
                </Button>
              ) : null}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row actions derive from t/locale and the mutation handles
    [t, i18n.language, canUpdate, canArchive, loadForEdit.isPending, restoreMutation.isPending],
  );

  const fieldError = (key: keyof TermDraft) =>
    errors[key] === undefined ? null : (
      <p role="alert" className="text-sm text-destructive">
        {errors[key]}
      </p>
    );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("partners.paymentTerms.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("partners.paymentTerms.subtitle")}</p>
        </div>
        {canCreate ? (
          <Button
            onClick={() => {
              setEditing(null);
              setDraft(EMPTY_DRAFT);
              setErrors({});
              setDialogOpen(true);
            }}
          >
            {t("partners.paymentTerms.new")}
          </Button>
        ) : null}
      </div>

      {termsQuery.isError ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          <span>{t("partners.paymentTerms.loadError")}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void termsQuery.refetch()}
          >
            {t("actions.retry")}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={terms}
          getRowId={(row) => row.id}
          storageKey="partners.payment-terms.columns"
          emptyMessage={t("partners.paymentTerms.empty")}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>
              {editing === null
                ? t("partners.paymentTerms.createTitle")
                : t("partners.paymentTerms.editTitle")}
            </DialogTitle>
            <DialogDescription>{t("partners.paymentTerms.subtitle")}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="term-code">{t("partners.paymentTerms.code")}</Label>
              <Input
                id="term-code"
                dir="ltr"
                autoComplete="off"
                value={draft.code}
                disabled={editing !== null}
                aria-invalid={errors.code !== undefined}
                onChange={(event) => set("code", event.target.value.toLowerCase())}
              />
              <p className="text-xs text-muted-foreground">{t("partners.paymentTerms.codeHint")}</p>
              {fieldError("code")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="term-name">{t("partners.paymentTerms.name")}</Label>
              <Input
                id="term-name"
                dir="auto"
                value={draft.name}
                aria-invalid={errors.name !== undefined}
                onChange={(event) => set("name", event.target.value)}
              />
              {fieldError("name")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="term-net-days">{t("partners.paymentTerms.netDays")}</Label>
              <Input
                id="term-net-days"
                dir="ltr"
                inputMode="numeric"
                value={draft.netDays}
                aria-invalid={errors.netDays !== undefined}
                onChange={(event) => set("netDays", event.target.value)}
              />
              {fieldError("netDays")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="term-day-of-month">{t("partners.paymentTerms.dayOfMonthDue")}</Label>
              <Input
                id="term-day-of-month"
                dir="ltr"
                inputMode="numeric"
                value={draft.dayOfMonthDue}
                aria-invalid={errors.dayOfMonthDue !== undefined}
                onChange={(event) => set("dayOfMonthDue", event.target.value)}
              />
              {fieldError("dayOfMonthDue")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="term-discount-pct">
                {t("partners.paymentTerms.earlyDiscountPct")}
              </Label>
              <Input
                id="term-discount-pct"
                dir="ltr"
                inputMode="decimal"
                value={draft.earlyDiscountPct}
                aria-invalid={errors.earlyDiscountPct !== undefined}
                onChange={(event) => set("earlyDiscountPct", event.target.value)}
              />
              {fieldError("earlyDiscountPct")}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="term-discount-days">{t("partners.paymentTerms.discountDays")}</Label>
              <Input
                id="term-discount-days"
                dir="ltr"
                inputMode="numeric"
                value={draft.discountDays}
                aria-invalid={errors.discountDays !== undefined}
                onChange={(event) => set("discountDays", event.target.value)}
              />
              {fieldError("discountDays")}
            </div>
          </div>

          {preview !== null ? (
            <p className="text-sm text-muted-foreground" data-testid="payment-term-preview">
              {preview}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={submit}
            >
              {editing === null
                ? t("partners.paymentTerms.create")
                : t("partners.paymentTerms.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archiveTarget !== null} onOpenChange={() => setArchiveTarget(null)}>
        <DialogContent closeLabel={t("actions.close")}>
          <DialogHeader>
            <DialogTitle>{t("partners.paymentTerms.archiveTitle")}</DialogTitle>
            <DialogDescription>{t("partners.paymentTerms.archiveBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setArchiveTarget(null)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={archiveMutation.isPending}
              onClick={() => {
                if (archiveTarget !== null) archiveMutation.mutate(archiveTarget.id);
              }}
            >
              {t("partners.common.archive")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
